import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  IconButton,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import { useLoading } from "../../../util/LoadingProvider";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Link } from "react-router-dom";

const schema = yup.object().shape({
  nameEn: yup.string().nullable(),
  nameTc: yup.string().nullable(),
  status: yup.boolean().nullable(),
});

const defaultFormValues = {
  nameEn: "",
  nameTc: "",
  status: true,
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",
    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    padding: theme.spacing(3),
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionText: {
    fontSize: "13px",
    fontWeight: 700,
    paddingLeft: "4px",
    marginBottom: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

export default function FaqCategoryEditingPage({ disabled, ...props }) {
  const { match, history } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();
  const classes = useStyles();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    console.log("Form submitted with values:", value);
    console.log("Current id:", id);
    // Check if we're in create mode (id is undefined or "create")
    if (!id || id === "create") {
      createFaqCategory(value);
    } else {
      updateFaqCategory(value);
    }
  };

  const onError = (errors) => {
    console.log("Form validation errors:", errors);
    // Show specific error messages if available
    const errorMessages = Object.keys(errors).map(key => {
      return errors[key]?.message || `${key} is invalid`;
    });
    if (errorMessages.length > 0) {
      toast.error(errorMessages[0]);
    } else {
      toast.error("Please fix the form errors before submitting.");
    }
  };

  const getFaqCategory = useCallback(async () => {
    // Don't fetch if id is "create" or undefined
    if (!id || id === "create") {
      return;
    }
    
    setLoading(true);
    try {
      const result = await apiHelper.cineclub.getMembershipFaqCategoryById(id);
      console.log(result);
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load FAQ Category.");
    }
    setLoading(false);
  }, [id, setLoading, reset]);

  const updateFaqCategory = async (value) => {
    try {
      setLoading(true);
      console.log("Updating FAQ Category with data:", value);
      const submitData = {
        nameEn: value.nameEn || "",
        nameTc: value.nameTc || "",
        status: value.status !== undefined ? value.status : true,
      };
      console.log("Submitting data:", submitData);
      const result = await apiHelper.cineclub.updateMembershipFaqCategory(id, submitData);
      console.log("Update result:", result);
      
      if (result.status === 200 || result.status === 201) {
        toast.dark("Your changes are saved.");
        // Refresh the FAQ Category data
        await getFaqCategory();
      } else {
        toast.error("Failed to update FAQ Category. Please try again.");
      }
    } catch (err) {
      console.error("Error updating FAQ Category:", err);
      toast.error("Failed to update FAQ Category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const createFaqCategory = async (value) => {
    try {
      setLoading(true);
      console.log("Creating FAQ Category with data:", value);
      const submitData = {
        nameEn: value.nameEn || "",
        nameTc: value.nameTc || "",
        status: value.status !== undefined ? value.status : true,
      };
      console.log("Submitting data:", submitData);
      const result = await apiHelper.cineclub.createMembershipFaqCategory(submitData);
      console.log("Create result:", result);
      
      if (result.status === 200 || result.status === 201) {
        // Extract the ID from the response
        const categoryId = result.data?.id || 
                      result.data?.data?.id || 
                      (result.data?.data && typeof result.data.data === 'object' && result.data.data.id) ||
                      (result.data && result.data.code === 0 && result.data.data?.id);
        
        if (categoryId) {
          toast.dark("The FAQ Category is created.");
          history.push(`/cineclub/faq/category/${categoryId}/edit`);
        } else {
          console.log("Could not extract FAQ Category ID from response, redirecting to list:", result.data);
          toast.dark("The FAQ Category is created.");
          // Redirect to listing page
          history.push(`/cineclub/faq/category`);
        }
      } else {
        toast.error("Failed to create FAQ Category. Please try again.");
      }
    } catch (err) {
      console.error("Error creating FAQ Category:", err);
      toast.error("Failed to create FAQ Category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch FAQ Category if we have a valid id (not in create mode)
    if (id && id !== "create") {
      getFaqCategory();
    }
  }, [getFaqCategory, id]);

  return (
    <div className={classes.content}>
      <Box className={classes.header}>
        <IconButton
          component={Link}
          to="/cineclub/faq/category"
          className={classes.backButton}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          FAQ Category &gt; {(!id || id === "create") ? "Add New FAQ Category" : "Edit FAQ Category"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="nameEn"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="nameTc"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name (Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "32px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value === true ? "ACTIVE" : value === false ? "INACTIVE" : "ACTIVE"}
                    onChange={(event, newValue) => {
                      if (newValue !== null) {
                        onChange(newValue === "ACTIVE");
                      }
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="ACTIVE"
                      aria-label="Active"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value="INACTIVE"
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>

        </fieldset>
        
        {!disabled && (
          <div className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              style={{
                color: "white",
                background: "#306F9D",
                marginRight: "5px",
                padding: "9px 16px",
              }}
            >
              Publish
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
